import { useEffect, useMemo, useRef, useState } from 'react';

export type UseVisibleSelectionResult<TId extends string> = {
  selectedIds: Set<TId>;
  selectedCount: number;
  selectedArray: TId[];
  clearSelection: () => void;

  selectAllCheckboxRef: React.RefObject<HTMLInputElement>;
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;

  toggleSelect: (id: TId) => void;
  toggleSelectAllVisible: () => void;
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<TId>>>;
};

/**
 * Row selection that is scoped to the currently visible IDs (current page / filter).
 * When the visible list changes, selection is pruned to only keep IDs still visible.
 */
export function useVisibleSelection<TId extends string>(visibleIds: TId[]): UseVisibleSelectionResult<TId> {
  const [selectedIds, setSelectedIds] = useState<Set<TId>>(new Set());
  // React will set it after render; we keep the ref type non-null for JSX compatibility.
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null!);

  // Prune selection when visible items change (pagination / filter changes)
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visibleSet = new Set(visibleIds);
      const next = new Set<TId>();
      for (const id of prev) {
        if (visibleSet.has(id)) next.add(id);
      }
      return next;
    });
  }, [visibleIds]);

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  useEffect(() => {
    if (!selectAllCheckboxRef.current) return;
    selectAllCheckboxRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected]);

  const toggleSelect = (id: TId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    selectedArray,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    someVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
    setSelectedIds,
  };
}

